import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { AnimatedDialog } from "@/components/reusable/AnimatedDialog";
import { resolveSchemaDisplayName } from "@/lib/knownMappingFilters";
import type { KnownMapping } from "@/types";

interface KnownMappingDetailPanelProps {
  mapping: KnownMapping | null;
  onOpenChange: (open: boolean) => void;
  /** Resolves a provider_id to its display name; falls back to the raw ID. */
  providerNameById: Map<string, string>;
  /** schema_id → human name (catalogue / assignments overlay). */
  schemaDisplayNameById?: Map<string, string>;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Row-click detail panel for a single known mapping.
 * Shows the source->target pairing plus attribution metadata (provider,
 * schema, who approved it and when) that the compact table row can't fit.
 */
export function KnownMappingDetailPanel({
  mapping,
  onOpenChange,
  providerNameById,
  schemaDisplayNameById = new Map(),
}: KnownMappingDetailPanelProps) {
  const { t } = useTranslation();

  if (!mapping) return null;

  const providerLabel = mapping.providerId
    ? (providerNameById.get(mapping.providerId) ?? mapping.providerId)
    : "—";

  const schemaLabel = resolveSchemaDisplayName(
    mapping.schemaId,
    mapping.schemaName,
    schemaDisplayNameById
  );

  const rows: Array<{ label: string; value: string }> = [
    { label: t("knownMappings.detail.source"), value: mapping.sourceField || "—" },
    { label: t("knownMappings.detail.target"), value: mapping.targetField || "—" },
    {
      label: t("knownMappings.detail.schema"),
      value: schemaLabel || "—",
    },
    { label: t("knownMappings.detail.provider"), value: providerLabel },
    {
      label: t("knownMappings.detail.confidence"),
      value: mapping.confidence != null ? `${Math.round(mapping.confidence * 100)}%` : "—",
    },
    { label: t("knownMappings.detail.approvedBy"), value: mapping.approvedBy || "—" },
    { label: t("knownMappings.detail.createdAt"), value: formatDate(mapping.createdAt) },
    { label: t("knownMappings.detail.updatedAt"), value: formatDate(mapping.updatedAt) },
  ];

  return (
    <AnimatedDialog open={!!mapping} onOpenChange={onOpenChange} maxWidth="max-w-lg">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <Dialog.Title className="text-base font-semibold text-[var(--color-text-primary)]">
            {t("knownMappings.detail.title")}
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors"
              aria-label={t("knownMappings.detail.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </div>

        <dl className="mt-4 divide-y divide-[var(--color-border-light)]">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between gap-4 py-2.5 text-sm">
              <dt className="text-[var(--color-text-secondary)]">{label}</dt>
              <dd className="text-right text-[var(--color-text-primary)] font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </AnimatedDialog>
  );
}
